/**
 * CONFIGURAZIONE DELL'AZIONE "crea attività" (`crm.create-task`).
 *
 * Scelte indipendenti — titolo, tipo, priorità, scadenza, ora, assegnatario e autore — senza
 * cascate: a differenza di "aggiorna campo", dove il tipo del campo scelto decide come si chiede il
 * valore, qui nessun campo cambia il significato degli altri. Ne segue un form piatto: i vocabolari
 * sono quelli del Task e vivono già nel client, e l'unica cosa che si va a chiedere al server è
 * l'elenco degli utenti, per i due campi che ne vogliono uno.
 *
 * ## Cosa finisce nella config
 *
 * `{ title, type, priority, dueInDays, time }` più, quando valorizzati,
 * `assignedUserId` e `userId`: le stesse chiavi che `CreateTaskAutomationActionType.validate`
 * rilegge. Tipo e priorità come **interi**, che è la forma che il provider Java pretende: la
 * conversione la fanno `taskTypeToDto`/`taskPriorityToDto`, gli stessi mapper del Task, così la
 * tabella dei codici resta una sola in tutto il frontend.
 *
 * I due campi utente sono facoltativi e la loro chiave viene **omessa** quando non c'è una scelta,
 * invece di essere scritta a zero: per il provider Java sarebbe lo stesso, ma un id utente
 * inesistente scritto a database si legge peggio di un campo assente.
 *
 * ## La scadenza è un NUMERO DI GIORNI, non una data
 *
 * Un'automazione scatta in un momento che chi la configura non conosce e che si ripete: una data
 * fissa sarebbe già passata al secondo giro e l'attività nascerebbe scaduta. La combo offre quindi
 * le stesse scorciatoie del form del Task — oggi, domani, entro 3 giorni, entro 1 settimana — ma
 * quello che salva è il numero, e la voce "personalizzata" resta fuori perché apre un selettore di
 * data. Il motore somma quei giorni all'istante dell'esecuzione.
 *
 * Nella config va il numero e non il codice del preset: un preset è una comodità di questo form,
 * non un vocabolario di dominio, e salvarne il nome obbligherebbe il Java a conoscerlo — cioè a
 * tenere una seconda copia dell'elenco. Con il numero, aggiungere "fra due settimane" alla tendina
 * non tocca il backend e non invalida le configurazioni esistenti.
 *
 * ## Perché tutto passa da ChipSelect e input nativi
 *
 * Il contratto dei form di configurazione non porta un flag di sola lettura: chi li ospita li
 * avvolge in un `<fieldset disabled>`, che spegne da sé i controlli di form. `ChipSelect` è un
 * `<button>` vero e l'ora è un `<input type="time">` vero, quindi in consultazione si spengono
 * entrambi senza che questo file se ne occupi. La `Select` di MUI — che il form del Task usa per
 * lo stesso preset — non andrebbe bene qui: mostra un `div[role=combobox]` e resterebbe viva.
 *
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { useServerPagedSearch } from '@crm/shared';
import { useCurrentGroupId } from '../../../auth/authSelectors';
import { userApi } from '../../user/userApi';
import type { User, UserOption } from '../../user/userTypes';
import {
  taskPriorityFromDto,
  taskPriorityToDto,
  taskTypeFromDto,
  taskTypeToDto,
} from '../../task/taskMappers';
import {
  DEADLINE_PRESETS,
  TASK_PRIORITIES,
  TASK_TYPES,
  type TaskPriority,
  type TaskType,
} from '../../task/taskTypes';
import { ChipSelect } from '../catalogFieldEditors';
import type { ActionConfigProps } from './automationActionConfig';

/**
 * Valori con cui il form si presenta su un'azione appena aggiunta. Non vengono scritti al mount —
 * nessun effetto che sporchi la configurazione senza che l'utente abbia toccato nulla — ma entrano
 * nella config alla prima modifica, perché `emit` la riscrive intera. Chi non tocca niente lascia la
 * config a `null`, e la validazione lo dirà chiedendo il titolo, che è comunque obbligatorio.
 */
const DEFAULT_DUE_IN_DAYS = 3;
const DEFAULT_PRIORITY: TaskPriority = 'none';
const DEFAULT_TIME = '17:00';
const DEFAULT_TYPE: TaskType = 'activity';

/** Le sole scorciatoie relative: "personalizzata" apre un selettore di data e qui non ha senso. */
const RELATIVE_PRESETS = DEADLINE_PRESETS.filter((preset) => preset.days !== null);

/**
 * Chiavi dei due campi FACOLTATIVI. Costanti e non letterali perché ciascuna compare in tre punti —
 * lettura dalla config, scrittura, e cancellazione quando il campo si svuota — e un refuso in uno
 * solo dei tre non fallirebbe la compilazione: produrrebbe una config con una chiave che il
 * provider Java non legge. Le obbligatorie non ne hanno bisogno, compaiono una volta sola nella
 * forma abbreviata dell'oggetto.
 */
const ASSIGNED_USER_ID = 'assignedUserId';
const USER_ID = 'userId';

/**
 * Scelta di un utente con ricerca, per i due campi facoltativi dell'azione.
 *
 * Vive qui come componente locale invece che duplicato due volte nel form: assegnatario e autore
 * hanno lo stesso identico bisogno — cercare fra gli utenti del sito e ricordare un id — e l'unica
 * cosa che cambia è l'etichetta.
 *
 * ## In configurazione va SOLO l'id
 *
 * Non anche il nome: `userApi` tiene già in cache l'elenco degli utenti del sito, quindi risolvere
 * un id costa nulla, mentre un'etichetta copiata nella config diventerebbe falsa alla prima
 * rinomina. Vale la stessa regola dei preset di scadenza — nella config va il dato, non ciò che si
 * vede.
 *
 * ## Perché un Autocomplete e non un ChipSelect
 *
 * Gli altri campi di questo form usano `ChipSelect`, che non ha ricerca: su un sito con molti
 * utenti sarebbe una tendina da scorrere. Questo è anche il controllo che l'utente già conosce
 * dalla creazione manuale di un'attività. Resta compatibile con la sola lettura: MUI rende un
 * `<input>` vero, quindi il `<fieldset disabled>` di chi ospita il form lo spegne come spegne i
 * chip.
 *
 * ## L'utente cancellato non si nasconde
 *
 * Se l'id in configurazione non corrisponde più a nessuno, il campo resta vuoto e l'id NON viene
 * cancellato dalla config: sparire in silenzio farebbe credere che il campo non fosse mai stato
 * valorizzato, mentre così la validazione lato server lo dice ("Inserire un assegnatario valido").
 */
function UserPicker({
  label,
  hint,
  placeholder,
  userId,
  onPick,
}: {
  label: string;
  /** Cosa succede lasciando il campo vuoto: i due default non sono la stessa cosa. */
  hint: string;
  /**
   * Testo a campo vuoto. Dice il DEFAULT e non ripete l'etichetta, che ora sta fuori dal
   * controllo: è la versione breve di {@link hint}, e a differenza sua si vede senza passarci
   * sopra col mouse.
   */
  placeholder: string;
  /** Id attualmente in configurazione; `0` o assente = campo non valorizzato. */
  userId: number;
  /** `0` quando l'utente svuota il campo. */
  onPick: (userId: number) => void;
}) {
  const groupId = useCurrentGroupId();
  const [selected, setSelected] = useState<UserOption | null>(null);
  const [open, setOpen] = useState(false);

  // Il backend degli utenti non è paginato: userApi.search filtra la lista del sito già in cache,
  // e qui la si impagina lato client — stessa mossa di TaskForm, da cui questo blocco è ripreso.
  // `enabled` legato all'APERTURA della tendina: senza, montare il form farebbe partire la ricerca
  // anche quando l'azione si sta solo consultando, dove nessuno cercherà nulla. Il costo di rete
  // vero è comunque uno solo — fetchGroupUsers cachea per sito — ma tanto vale non pagarlo affatto
  // finché non serve.
  const {
    loading,
    autocompleteProps,
    listboxProps,
    setQuery,
  } = useServerPagedSearch<UserOption>({
    enabled: open,
    fetchPage: async (query, page, size) => {
      const list = await userApi.search(query, groupId);
      const start = (page - 1) * size;

      return {
        items: list.slice(start, start + size).map((user: User) => ({
          id: user.id,
          label: user.fullName,
        })),
        totalCount: list.length,
      };
    },
    pageSize: 20,
    debounceMs: 300,
    resetOn: [groupId],
  });

  // Idratazione dell'etichetta a partire dall'id salvato: la config porta un numero, la tendina
  // deve mostrare un nome. Non costa una chiamata di rete in più, perché getById legge la stessa
  // lista che la ricerca ha già scaricato e messo in cache.
  // La guardia sull'id evita di rifare il lavoro a ogni render, e `cancelled` di scrivere su un
  // componente smontato quando la modale si chiude mentre la lookup è in volo.
  // `selectedId` è estratto invece di scrivere `selected?.id` fra le dipendenze: con
  // un'espressione lì dentro `react-hooks/exhaustive-deps` smette di verificare l'array, e con
  // esso la garanzia che una dipendenza aggiunta domani venga segnalata.
  const selectedId = selected?.id;

  useEffect(() => {
    if (!userId) {
      setSelected(null);

      return;
    }

    if (selectedId === userId) return;

    let cancelled = false;

    userApi
      .getById(userId, groupId)
      .then((user) => {
        if (!cancelled) setSelected({ id: user.id, label: user.fullName });
      })
      // Utente cancellato dopo il salvataggio: il campo resta vuoto e l'id resta in config, così
      // la validazione può dirlo invece di far sparire il problema.
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [userId, groupId, selectedId]);

  // Etichetta FUORI dal controllo, come per i chip accanto, invece della label flottante di MUI:
  // era l'unico campo del pannello a portarsela dentro, e bastava a farlo sembrare di un'altra
  // famiglia. Il segnaposto dice la stessa cosa quando il campo è vuoto.
  return (
    <>
      {/* Il suggerimento sta sull'ETICHETTA e non sull'intero campo: avvolgere anche la tendina
          farebbe comparire il fumetto mentre si sceglie, sopra le voci che si stanno leggendo.
          Tooltip di MUI e non l'attributo `title`, come già fanno il flag del riscatto nel builder,
          AutomationList e la Sidebar: quello nativo non si stila, compare in ritardo e su un testo
          lungo diventa un blocco illeggibile. */}
      <Tooltip title={hint} placement="top" arrow>
        <span className="text-sm text-muted">{label}</span>
      </Tooltip>
      {/* `autocompleteProps` NON si spreads: porta anche `inputValue`, che renderebbe il testo del
          campo CONTROLLATO dall'hook. E l'hook ignora di proposito `reason === 'reset'` — la
          notifica con cui MUI segnala un `value` cambiato da codice — per proteggere la propria
          query. Il risultato era che l'idratazione impostava il valore ma il campo restava vuoto:
          l'hook è scritto per una casella di ricerca, non per un selettore con un valore
          persistito.
          Lasciando il testo a MUI, il campo mostra da sé l'etichetta dell'opzione selezionata,
          mentre all'hook si passa la sola query digitata — che è tutto ciò che gli serve per
          cercare. Si prendono quindi le due props utili e non il pacchetto intero. */}
      <Autocomplete
        options={autocompleteProps.options}
        filterOptions={autocompleteProps.filterOptions}
        onInputChange={(_, next, reason) => {
          // 'input' e 'clear' sono le due transizioni guidate dall'utente che cambiano cosa sta
          // cercando; 'reset' e 'selectOption' riguardano il valore, non la ricerca.
          if (reason === 'input' || reason === 'clear') setQuery(next);
        }}
        value={selected}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        onChange={(_, picked) => {
          setSelected(picked);
          onPick(picked?.id ?? 0);
        }}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        loading={loading}
        slotProps={{ listbox: listboxProps }}
        // NON passare `slotProps.htmlInput` qui dentro: `params.inputProps` porta anche il REF
        // dell'input, e in MUI v9 dichiarare lo slot scavalca quel canale — `Autocomplete` non
        // trova più il proprio `<input>` ("Unable to find the input element") e smette di
        // comportarsi da tendina. Provato, e rompe: `params` va spreadato e basta.
        // Ne consegue che `aria-label` resta sulla TextField, cioè sul FormControl radice invece
        // che sull'input. È un difetto di accessibilità NOTO e accettato per ora: il nome
        // accessibile andrebbe dato legando l'etichetta qui accanto con `htmlFor` all'id che
        // Autocomplete genera, ed è una modifica da fare potendola provare a video.
        renderInput={(params) => <TextField {...params} placeholder={placeholder} />}
        // Classe propria e non `form-field`: quella porta un margin-bottom pensato per lo stack
        // verticale dei form MUI, che dentro una `builder-crow` — flex con align-items:center —
        // scentrerebbe il campo rispetto al resto della riga. Ci si appende anche la resa a
        // pastiglia che allinea il controllo ai `.builder-fieldnum` vicini.
        className="builder-userpicker"
        size="small"
      />
    </>
  );
}

export default function CreateTaskActionConfig({ config, onChange }: ActionConfigProps) {
  const { t } = useTranslation();

  // `as … | undefined` e non un cast secco: la config è opaca (`Record<string, unknown>`), quindi
  // l'asserzione è nostra — dichiararla nullable dice il vero e lascia lavorare il `??`.
  const title = (config?.title as string | undefined) ?? '';
  const type = (config?.type as number | undefined) ?? taskTypeToDto(DEFAULT_TYPE);
  const priority = (config?.priority as number | undefined) ?? taskPriorityToDto(DEFAULT_PRIORITY);
  const dueInDays = (config?.dueInDays as number | undefined) ?? DEFAULT_DUE_IN_DAYS;
  const time = (config?.time as string | undefined) ?? DEFAULT_TIME;
  const assignedUserId = (config?.[ASSIGNED_USER_ID] as number | undefined) ?? 0;
  const userId = (config?.[USER_ID] as number | undefined) ?? 0;

  /**
   * La config si riscrive INTERA a ogni modifica, non a delta: è il form l'unico a conoscerne la
   * forma, e restituirne una parziale obbligherebbe chi lo ospita a saperla ricomporre. Parte però
   * da quella corrente invece che da zero, per non buttare via chiavi che questo form non gestisce.
   *
   * I due campi utente sono FACOLTATIVI e quando non sono valorizzati la chiave viene **omessa**
   * invece che messa a zero. Lo zero funzionerebbe lo stesso — il provider Java lo legge come
   * "assente" — ma sarebbe un segnaposto scritto a database, e chi guardasse la configurazione
   * vedrebbe un id utente che non esiste invece di un campo che non c'è. Stessa scelta della chiave
   * `value` in `UpdateFieldActionConfig`.
   */
  const emit = (next: Record<string, unknown>) => {
    const merged: Record<string, unknown> = {
      ...(config ?? {}),
      title,
      type,
      priority,
      dueInDays,
      time,
      [ASSIGNED_USER_ID]: assignedUserId,
      [USER_ID]: userId,
      ...next,
    };

    for (const key of [ASSIGNED_USER_ID, USER_ID]) {
      if (!merged[key]) delete merged[key];
    }

    onChange(merged);
  };

  return (
    <div className="builder-grid">
      <div className="builder-crow builder-crow--full">
        <span className="builder-fieldnum builder-fieldnum--grow">
          <input
            type="text"
            value={title}
            aria-label={t('automation.form.taskTitle')}
            placeholder={t('automation.form.taskTitle')}
            onChange={(e) => emit({ title: e.target.value })}
          />
        </span>
      </div>

      <div className="builder-crow">
        {/* Tipo e priorità viaggiano come interi nella config ma si scelgono per nome: la tendina
            mostra le stesse voci del form del Task, con le sue chiavi i18n. */}
        {/* La voce scelta si ritrova nell'elenco invece di essere convertita con un cast: le
            opzioni nascono da `TASK_TYPES`, quindi il valore tipizzato ce l'ha già l'elemento
            trovato. Un valore che non corrisponde a nulla viene ignorato, dove un cast lo avrebbe
            spacciato per buono e fatto persistere un intero inesistente. */}
        {/* Etichetta VISIBILE accanto a ogni chip, non solo l'ariaLabel: un chip mostra il valore
            scelto — "Attività", "Nessuna" — e senza un nome davanti chi legge non sa di cosa sia il
            valore. Stessa resa della riga "In caso di errore" nella modale che ci ospita. */}
        <span className="text-sm text-muted">{t('common.type')}</span>
        <ChipSelect
          value={taskTypeFromDto(type)}
          options={TASK_TYPES.map((taskType) => ({
            value: taskType.value,
            label: t(taskType.label),
          }))}
          ariaLabel={t('automation.form.taskType')}
          onChange={(next) => {
            const picked = TASK_TYPES.find((taskType) => taskType.value === next);

            if (picked) emit({ type: taskTypeToDto(picked.value) });
          }}
        />

        <span className="text-sm text-muted">{t('task.priority')}</span>
        <ChipSelect
          value={taskPriorityFromDto(priority)}
          options={TASK_PRIORITIES.map((taskPriority) => ({
            value: taskPriority.value,
            label: t(taskPriority.label),
          }))}
          ariaLabel={t('task.priority')}
          onChange={(next) => {
            const picked = TASK_PRIORITIES.find((taskPriority) => taskPriority.value === next);

            if (picked) emit({ priority: taskPriorityToDto(picked.value) });
          }}
        />
      </div>

      <div className="builder-crow">
        {/* Il valore del chip è il NUMERO reso stringa, perché è quello che finisce in config: la
            voce scelta si ritrova per giorni, non per codice. Ne segue che una config con un numero
            fuori elenco — scritta via REST, o rimasta da una tendina che offriva altro — non
            corrisponde a nessuna opzione e il chip mostra il segnaposto invece di mentire. */}
        {/* Etichetta "Scadenza" e non "Data di scadenza" come nel form del Task: qui non si sceglie
            una data ma un numero di giorni, e chiamarla data direbbe una cosa che non è. */}
        <span className="text-sm text-muted">{t('automation.form.taskDeadline')}</span>
        <ChipSelect
          value={String(dueInDays)}
          options={RELATIVE_PRESETS.map((preset) => ({
            value: String(preset.days),
            label: t(preset.label),
          }))}
          placeholder={t('automation.form.taskDeadline')}
          ariaLabel={t('automation.form.taskDeadline')}
          onChange={(next) => emit({ dueInDays: Number(next) })}
        />

        {/* Stesso controllo del form del Task: un `input type="time"`, che è anche ciò che la
            TextField di MUI rende là sotto. Nativo qui perché la modale non ha bisogno della
            decorazione di MUI e perché resta un controllo di form vero, quindi il fieldset lo
            spegne in consultazione. */}
        <span className="text-sm text-muted">{t('task.deadlineTime')}</span>
        {/* Modificatore `--time`: i 52px di `.builder-fieldnum` sono tagliati su un numero breve e
            su un input orario troncavano i minuti, che è la metà del dato. */}
        <span className="builder-fieldnum builder-fieldnum--time">
          <input
            type="time"
            value={time}
            aria-label={t('task.deadlineTime')}
            onChange={(e) => emit({ time: e.target.value })}
          />
        </span>
      </div>

      {/* I due campi FACOLTATIVI, in fondo: sono quelli che quasi nessuno tocca — l'autore lasciato
          vuoto significa l'utente predefinito dell'istanza, che è la scelta giusta quasi sempre — e
          metterli in mezzo agli obbligatori li farebbe sembrare da compilare.
          UNA RIGA CIASCUNO e non affiancati: un nome e cognome è più lungo di un chip, e in due su
          una riga il valore veniva troncato. Gli altri campi si affiancano perché i loro valori
          sono brevi e di lunghezza prevedibile; questi no, dipendono da come si chiamano le
          persone. */}
      <div className="builder-crow builder-crow--wide">
        <UserPicker
          label={t('task.assignedTo')}
          hint={t('automation.form.taskAssigneeHint')}
          placeholder={t('automation.form.taskAssigneePlaceholder')}
          userId={assignedUserId}
          onPick={(picked) => emit({ [ASSIGNED_USER_ID]: picked })}
        />
      </div>

      <div className="builder-crow builder-crow--wide">
        <UserPicker
          label={t('automation.form.taskAuthor')}
          hint={t('automation.form.taskAuthorHint')}
          placeholder={t('automation.form.taskAuthorPlaceholder')}
          userId={userId}
          onPick={(picked) => emit({ [USER_ID]: picked })}
        />
      </div>
    </div>
  );
}
